<template>
  <div class="tooth-chart" :class="{ 'tooth-chart--select': selectMode, 'tooth-chart--primary': template === 'primary' }">
    <div v-for="arch in arches" :key="arch.key" class="tooth-chart__arch" :class="'tooth-chart__arch--' + arch.key">
      <div v-for="quadrant in arch.quadrants" :key="quadrant.key" class="tooth-chart__quadrant" :class="'tooth-chart__quadrant--' + quadrant.key">
        <button v-for="toothNumber in quadrant.teeth" :key="toothNumber"
                type="button"
                class="tooth-tile"
                :class="tileClass(toothNumber)"
                :aria-pressed="isSelected(toothNumber) ? 'true' : 'false'"
                :title="tileTitle(toothNumber)"
                @click="$emit('tooth-click', toothNumber)">
          <span class="tooth-tile__image">
            <img :src="imageUrl(toothNumber)" :alt="toothNumber" loading="lazy" draggable="false" />
          </span>
          <span class="tooth-tile__number">{{ toothNumber }}</span>
          <!-- Anahtarlar şart: FontAwesome JS <i>'yi <svg> ile değiştirir; Vue anahtarsız
               kardeş span'ları birbirine yamarsa eski ikon DOM'da asılı kalır. -->
          <span v-if="treatmentCount(toothNumber) > 0" key="badge" class="tooth-tile__badge">{{ treatmentCount(toothNumber) }}</span>
          <span v-if="isSelected(toothNumber)" key="check" class="tooth-tile__check"><i class="fa fa-check" aria-hidden="true"></i></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { TOOTH_CHART_LAYOUT, toothImageUrl } from './toothConfig'

  export default {
    name: 'ToothChart',
    props: {
      template: { type: String, default: 'permanent' },
      // { [toothNumber]: eklenmiş hizmet satırı sayısı }
      treatmentCounts: { type: Object, default: () => ({}) },
      selectedTeeth: { type: Array, default: () => [] },
      highlightedTeeth: { type: Array, default: () => [] },
      accentTooth: { type: Number, default: null },
      selectMode: { type: Boolean, default: false }
    },
    computed: {
      layout() {
        return TOOTH_CHART_LAYOUT[this.template] || TOOTH_CHART_LAYOUT.permanent
      },
      arches() {
        return [
          { key: 'upper', quadrants: [{ key: 'right', teeth: this.layout.upper.right }, { key: 'left', teeth: this.layout.upper.left }] },
          { key: 'lower', quadrants: [{ key: 'right', teeth: this.layout.lower.right }, { key: 'left', teeth: this.layout.lower.left }] }
        ]
      }
    },
    methods: {
      imageUrl(toothNumber) {
        return toothImageUrl(toothNumber)
      },
      treatmentCount(toothNumber) {
        return this.treatmentCounts[toothNumber] || 0
      },
      isSelected(toothNumber) {
        return this.selectedTeeth.indexOf(toothNumber) !== -1
      },
      tileClass(toothNumber) {
        return {
          'tooth-tile--treated': this.treatmentCount(toothNumber) > 0,
          'tooth-tile--selected': this.isSelected(toothNumber),
          'tooth-tile--highlight': this.highlightedTeeth.indexOf(toothNumber) !== -1,
          'tooth-tile--accent': this.accentTooth === toothNumber
        }
      },
      tileTitle(toothNumber) {
        const count = this.treatmentCount(toothNumber)
        return count > 0 ? `${this.$t('Tooth')} ${toothNumber} · ${count} ${this.$t('Treatment')}` : `${this.$t('Tooth')} ${toothNumber}`
      }
    }
  }
</script>

<style scoped>
  .tooth-chart {
    --tooth-primary: #089bab;
    --tooth-primary-soft: #e3f4f6;
    --tooth-border: #e1e8ea;
    --tooth-text: #374948;
    --tooth-muted: #8a9a99;
    --tooth-accent: #fd6256;
    --tooth-highlight: #ffb057;
    display: flex;
    flex-direction: column;
    gap: 18px;
    user-select: none;
  }

  .tooth-chart__arch {
    display: flex;
    justify-content: center;
    gap: 0;
  }

  .tooth-chart__arch--upper {
    padding-bottom: 14px;
    border-bottom: 1px dashed var(--tooth-border);
  }

  .tooth-chart__quadrant {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 6px;
    flex: 1 1 0;
    max-width: 440px;
    padding: 0 10px;
  }

  .tooth-chart__quadrant--right {
    border-right: 1px dashed var(--tooth-border);
  }

  .tooth-chart--primary .tooth-chart__quadrant {
    max-width: 300px;
  }

  .tooth-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 0;
    padding: 8px 2px 6px;
    background: #fff;
    border: 1px solid var(--tooth-border);
    border-radius: 14px;
    color: var(--tooth-text);
    cursor: pointer;
    transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease, background-color .15s ease;
  }

  .tooth-chart__arch--lower .tooth-tile {
    flex-direction: column-reverse;
  }

  .tooth-tile:hover,
  .tooth-tile:focus-visible {
    border-color: var(--tooth-primary);
    box-shadow: 0 6px 16px rgba(8, 155, 171, .16);
    transform: translateY(-2px);
    outline: none;
  }

  .tooth-tile__image {
    display: block;
    width: 100%;
    aspect-ratio: 5 / 8;
    overflow: hidden;
  }

  .tooth-tile__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    pointer-events: none;
    transition: transform .15s ease;
  }

  .tooth-chart--primary .tooth-tile__image img {
    transform: scale(.86);
  }

  .tooth-tile__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    color: var(--tooth-muted);
    background: #f3f6f7;
  }

  .tooth-tile__badge {
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    color: #fff;
    background: var(--tooth-primary);
  }

  .tooth-chart__arch--lower .tooth-tile__badge {
    top: auto;
    bottom: 4px;
  }

  .tooth-tile__check {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 10px;
    line-height: 18px;
    color: #fff;
    background: var(--tooth-primary);
  }

  .tooth-chart__arch--lower .tooth-tile__check {
    top: auto;
    bottom: 4px;
  }

  .tooth-tile--treated {
    background: var(--tooth-primary-soft);
    border-color: rgba(8, 155, 171, .35);
  }

  .tooth-tile--treated .tooth-tile__number {
    color: #fff;
    background: var(--tooth-primary);
  }

  .tooth-tile--selected {
    border: 2px solid var(--tooth-primary);
    box-shadow: 0 0 0 3px rgba(8, 155, 171, .15);
    padding: 7px 1px 5px;
  }

  .tooth-chart--select .tooth-tile:not(.tooth-tile--selected) {
    border-style: dashed;
  }

  .tooth-tile--highlight {
    border-color: var(--tooth-highlight);
    box-shadow: 0 0 0 3px rgba(255, 176, 87, .25);
  }

  .tooth-tile--accent {
    border-color: var(--tooth-accent);
    box-shadow: 0 0 0 3px rgba(253, 98, 86, .22);
  }

  .tooth-tile--accent .tooth-tile__number {
    background: var(--tooth-accent);
    color: #fff;
  }

  @media (max-width: 991.98px) {
    .tooth-chart__quadrant {
      gap: 4px;
      padding: 0 6px;
    }

    .tooth-tile {
      border-radius: 10px;
      padding: 6px 1px 4px;
    }

    .tooth-tile__number {
      min-width: 0;
      padding: 0 4px;
      font-size: 11px;
    }
  }

  /* Telefonda bir çene iki satıra iner: önce hastanın sağ çeyreği, altında sol çeyreği. */
  @media (max-width: 575.98px) {
    .tooth-chart__arch {
      flex-direction: column;
      gap: 10px;
    }

    .tooth-chart__quadrant {
      max-width: none;
      padding: 0;
    }

    .tooth-chart__quadrant--right {
      border-right: 0;
    }

    .tooth-tile__badge,
    .tooth-tile__check {
      display: none;
    }
  }
</style>
